import type { WordBox } from "../api/types";

/** One selectable word of a scanned page, in fractions (0..1) of the page size. */
export interface OcrSpan {
  /** The word, with a trailing space unless it ends its line. */
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
  /** Last word of its line: the copied text gets a line break after it. */
  eol: boolean;
}

/**
 * Tesseract emits words in reading order but the stored boxes carry no line numbers: a line ends
 * where the next word starts left of the previous one or leaves its band vertically.
 */
function sameLine(a: WordBox, b: WordBox): boolean {
  if (b[1] < a[1]) return false;
  const overlap = Math.min(a[4], b[4]) - Math.max(a[2], b[2]);
  return overlap > 0.5 * Math.min(a[4] - a[2], b[4] - b[2]);
}

/**
 * Lays the OCR words out as a text layer: each word spans its line's full height and reaches the
 * next word of the line, so dragging across a line never falls into a gap between words.
 */
export function layoutOcrWords(words: readonly WordBox[]): OcrSpan[] {
  const lines: WordBox[][] = [];
  for (const w of words) {
    if (!w[0].trim() || w[3] <= w[1] || w[4] <= w[2]) continue;
    const line = lines.at(-1);
    const prev = line?.at(-1);
    if (line && prev && sameLine(prev, w)) line.push(w);
    else lines.push([w]);
  }
  const spans: OcrSpan[] = [];
  for (const line of lines) {
    const top = Math.min(...line.map((w) => w[2]));
    const bottom = Math.max(...line.map((w) => w[4]));
    line.forEach((w, i) => {
      const next = line[i + 1];
      const right = next ? Math.max(next[1], w[3]) : w[3];
      spans.push({
        text: next ? `${w[0]} ` : w[0],
        left: w[1],
        top,
        width: right - w[1],
        height: bottom - top,
        eol: !next,
      });
    });
  }
  return spans;
}

/** Joins the selected parts of the words: lines end with a line break, edges are trimmed. */
export function joinSelected(parts: readonly { text: string; eol: boolean }[]): string {
  let out = "";
  parts.forEach((p, i) => {
    out += p.text;
    if (p.eol && i < parts.length - 1) out = `${out.trimEnd()}\n`;
  });
  return out.trim();
}
